import { Link } from "@tanstack/react-router";
import { Button } from "@wags/ui/button.tsx";
import {
    listingContactPreview,
    type WagsListingBatchRecord,
} from "@wags/catalog/pure.ts";
import {
    type ContactLinkCard,
    formatContactLinkCard,
} from "@wags/provider-contact-sessions/contact-message.ts";
import { PageErrorState } from "@/components/shared/ErrorState";
import { seoMeta } from "@/lib/ui/seo";

export const PRIVATE_LINK_HEADERS = {
    "Cache-Control": "private, no-store, max-age=0",
    "Referrer-Policy": "no-referrer",
    "X-Robots-Tag": "noindex, nofollow",
};

export function privateLinkHead(title: string) {
    return {
        meta: [{ title }, { name: "robots", content: "noindex, nofollow" }],
    };
}

export type ContactLinkShareImage = {
    url: string | null;
    mimeType: "image/png" | null;
};

export function contactLinkShareImage(
    linkPreview: ContactLinkCard,
    generatedOgImageUrl: string | null | undefined,
): ContactLinkShareImage {
    return generatedOgImageUrl
        ? { url: generatedOgImageUrl, mimeType: "image/png" }
        : { url: linkPreview.imageUrl, mimeType: null };
}

export function contactLinkPreview(
    listing:
        | Pick<
              WagsListingBatchRecord,
              | "id"
              | "title"
              | "breedNames"
              | "breeds"
              | "media"
              | "primaryPhotoId"
              | "organization"
              | "ogImageUrl"
          >
        | undefined,
) {
    if (!listing) return null;
    const preview = listingContactPreview(
        { ...listing, listingBreedNames: listing.breedNames },
        { name: listing.organization?.name?.trim() },
    );
    const linkPreview = formatContactLinkCard({
        title: listing.title,
        breedNames: preview.breedNames,
        imageUrl: preview.imageUrl,
        providerName: preview.provider?.name,
    });
    return {
        listingId: listing.id,
        linkPreview,
        shareImage: contactLinkShareImage(linkPreview, listing.ogImageUrl),
    };
}

export function contactLinkHead(
    resolved: ReturnType<typeof contactLinkPreview> | undefined,
) {
    if (!resolved)
        return privateLinkHead("This message link has expired | Wags");
    const { linkPreview, shareImage } = resolved;
    return seoMeta({
        title: linkPreview.title || "A dog on Wags",
        description: linkPreview.subtitle || "See this dog on Wags.",
        url: `/dog/${encodeURIComponent(resolved.listingId)}`,
        image: shareImage.url ?? undefined,
        imageMimeType: shareImage.mimeType ?? undefined,
        type: "product",
        noindex: true,
        nofollow: true,
    });
}

const EXPIRED_LINK_DESCRIPTION = {
    c: "Ask the person who shared it to send a new contact link, or return to Wags.",
    k: "Open the dog's page on Wags and tap Reach out to start a new message.",
};

export function ContactLinkExpired({ kind }: { kind: "c" | "k" }) {
    return (
        <main className="min-h-dvh bg-maltese-white text-brown">
            <PageErrorState
                className="min-h-dvh"
                title="This message link has expired"
                description={EXPIRED_LINK_DESCRIPTION[kind]}
                scene="waiting"
                actions={
                    <Button asChild>
                        <Link to="/find">Return to Wags</Link>
                    </Button>
                }
            />
        </main>
    );
}
